import React, { useCallback, useEffect, useState } from 'react';
import { validateBoardEditorial, type AdminBoardEditorialDto } from '@living-library/contracts';
import { boardError, type BoardRequest } from './board-api';
import { button, c, card, Completeness, emptyText, Status } from './board-ui';
import { BoardEditor } from './board-editor';
import { newQuestion } from './board-content-editor';

export function newBoardDraft(kind: 'picks' | 'quiz'): AdminBoardEditorialDto {
  const now = new Date(),
    start = new Date(now.getTime() + 86400000);
  return {
    id: '',
    kind,
    status: 'draft',
    expectedVersion: 0,
    period:
      kind === 'picks'
        ? `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
        : null,
    publishAt: null,
    startsAt: kind === 'quiz' ? start.toISOString() : null,
    endsAt: kind === 'quiz' ? new Date(start.getTime() + 14 * 86400000).toISOString() : null,
    title: emptyText(),
    intro: emptyText(),
    hostId: null,
    items: [],
    questions: kind === 'quiz' ? Array.from({ length: 3 }, newQuestion) : [],
    answerCount: 0,
    hasVotes: false,
    counts: [],
    issues: [],
  };
}
export function BoardList({ kind, request }: { kind: 'picks' | 'quiz'; request: BoardRequest }) {
  const [items, setItems] = useState<AdminBoardEditorialDto[]>([]),
    [selected, setSelected] = useState<AdminBoardEditorialDto | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      setItems(await request(kind === 'picks' ? '/picks' : '/quizzes'));
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }, [kind, request]);
  useEffect(() => {
    void load();
  }, [load]);
  if (selected)
    return (
      <BoardEditor
        initial={selected}
        request={request}
        onClose={() => {
          setSelected(null);
          void load();
        }}
      />
    );
  return (
    <section>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-3xl">{kind === 'picks' ? c.picks : c.quizzes}</h2>
        <div className="flex flex-wrap gap-2">
          <button className={button} disabled={busy} onClick={() => void load()}>
            {c.refresh}
          </button>
          <button className="admin-button-primary" onClick={() => setSelected(newBoardDraft(kind))}>
            {c.create}
          </button>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-warning">
          {error}
        </p>
      )}
      {busy && (
        <p className="admin-empty" role="status">
          {c.loading}
        </p>
      )}
      {!busy && !error && !items.length && <p className="admin-empty">{c.empty}</p>}
      <div className="grid gap-3">
        {items.map((item) => (
          <article
            key={item.id}
            className={`${card} flex flex-wrap items-center justify-between gap-4`}
          >
            <div className="min-w-0">
              <h3 className="mb-3 font-serif text-2xl">
                {kind === 'picks'
                  ? item.period
                  : item.title.en || item.title.sk || item.title.cs || c.statuses.draft}
              </h3>
              <Completeness issues={validateBoardEditorial(kind, item)} />
              {kind === 'quiz' && (
                <p className="mt-3 text-sm text-ink-muted">
                  {item.startsAt && new Date(item.startsAt).toLocaleString()} —{' '}
                  {item.endsAt && new Date(item.endsAt).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Status value={item.status} />
              <button className={button} onClick={() => setSelected(item)}>
                {c.edit}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
